import { useState, useMemo, useEffect } from "react";
import { Pode } from "@/lib/permissoes";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useNavegacao } from "@/lib/navegacao";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { updateColheitaSchema, type UpdateColheita, type CorFita, type ColheitaWithRelations, type Ciclo } from "@shared/schema";
import { useSetores } from "@/hooks/use-setores";
import { Plus, ShoppingBag, CheckCircle2, AlertCircle, XCircle, Pencil, Trash2, ChevronLeft, ChevronRight, Filter, X, Download, CircleOff, Share2, Copy, List, CalendarDays } from "lucide-react";
import { RegistrarColheitaDialog } from "@/components/registrar-colheita-dialog";
import * as XLSX from "xlsx";
import { agruparColheitasPorDia, textoWhatsAppColheita, type DiaColheita } from "@shared/resumo-dia";
import { semanaIso } from "@shared/boletim-periodo";
import { useColheitasPorDiaSetor } from "@/hooks/use-vendas";

import { PageHeader, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { PageSearch } from "@/components/layout/page-toolbar";
import { DataCard } from "@/components/data/data-card";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell, DataTableNum,
} from "@/components/data/data-table";
import { StatusBadge } from "@/components/data/status-badge";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { combina } from "@/lib/busca";
import { PageToolbar } from "@/components/layout/page-toolbar";
import { Segmented } from "@/components/layout/segmented";
// ⚠️ A semana ISO sai da STRING (`semanaIso`, em shared/boletim-periodo.ts), nunca de um
// `Date`. A cópia local que existia aqui construía o instante em UTC
// (`new Date(iso + 'T00:00:00Z')`) e lia dia/mês/ano com os getters LOCAIS — e o
// navegador do Hugo está em UTC-3. Resultado: toda colheita de SEGUNDA aparecia uma
// semana atrás (03/08/2026 mostrava "S31" em vez de "S32"), porque à meia-noite UTC ainda
// é domingo na Bahia. Este era o único caso desta família de bug que já estava visível em
// produção — os do servidor não aparecem porque a Vercel roda em UTC.

// "DD/MM/YYYY / SWW"
function formatColheitaDate(dateStr: string): string {
  const [ano, mes, dia] = dateStr.split("-");
  return `${dia}/${mes}/${ano} / S${semanaIso(dateStr).semana}`;
}

function getColheitaWeek(dateStr: string): number {
  return semanaIso(dateStr).semana;
}

// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook).
const PADRAO_COLHEITAS = {
  setor: "all",
  cor: "all",
  status: "all",
  semFita: "all",
  de: "",
  ate: "",
  visao: "lista",
  q: "",
  pagina: 1,
  porPagina: 25,
};
const PERMITIDOS_COLHEITAS = {
  status: ["all", "CICLOS_ATIVOS", "NO_PRAZO", "ATRASADA", "ANTECIPADA", "SEM_PLANO"],
  semFita: ["all", "true", "false"],
  visao: ["lista", "por-dia"],
  porPagina: [10, 25, 50, 100],
} as const;

export default function ColheitasPage() {
  const { abrir } = useNavegacao();
  // Recorte + página na URL: a Regra 1 só resolve Colheitas se `pagina` entrar
  // junto — voltar para a página 1 de uma lista de 25 linhas ainda é "achar de novo".
  const { estado: filtros, definir } = useEstadoNaUrl(PADRAO_COLHEITAS, PERMITIDOS_COLHEITAS);
  const currentPage = filtros.pagina;
  const itemsPerPage = filtros.porPagina;
  const filterSetor = filtros.setor;
  const filterCor = filtros.cor;
  const filterStatus = filtros.status;
  const filterSemFita = filtros.semFita;
  const filterDataInicio = filtros.de;
  const filterDataFim = filtros.ate;
  const visualizacao = filtros.visao as "lista" | "por-dia";
  const busca = filtros.q;
  const { toast } = useToast();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedColheita, setSelectedColheita] = useState<ColheitaWithRelations | null>(null);
  const [isReconciling, setIsReconciling] = useState(false); // Para reconciliação de colheitas sem fita
  
  // Pagination and filter states

  const [diaCompartilhar, setDiaCompartilhar] = useState<DiaColheita | null>(null);
  const [copiado, setCopiado] = useState(false);

  const { data: colheitas, isLoading: colheitasLoading } = useQuery<ColheitaWithRelations[]>({
    queryKey: ["/api/colheitas"],
  });

  // Lista completa para lookups históricos; produtivos para filtros de seleção
  const { setores } = useSetores();
  const { setores: setoresProdutivos } = useSetores({ somenteProdutivos: true });

  const { data: cores } = useQuery<CorFita[]>({
    queryKey: ["/api/cores"],
  });

  const { data: ciclos } = useQuery<Ciclo[]>({
    queryKey: ["/api/ciclos"],
  });

  const editForm = useForm<UpdateColheita>({
    resolver: zodResolver(updateColheitaSchema),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateColheita }) => {
      const response = await apiRequest("PATCH", `/api/colheitas/${id}`, data);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/colheitas"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ciclos"] });
      toast({
        title: "Colheita atualizada!",
        description: "As alterações foram salvas.",
      });
      setEditDialogOpen(false);
      setSelectedColheita(null);
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao atualizar colheita",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/colheitas/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/colheitas"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ciclos"] });
      toast({
        title: "Colheita excluída!",
        description: "O registro foi removido.",
      });
      setDeleteDialogOpen(false);
      setSelectedColheita(null);
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao excluir colheita",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onEditSubmit = (data: UpdateColheita) => {
    if (!selectedColheita) return;
    updateMutation.mutate({ id: selectedColheita.id, data });
  };

  const handleEdit = (colheita: ColheitaWithRelations) => {
    setSelectedColheita(colheita);
    setIsReconciling(false); // Reset reconciling state
    editForm.reset({
      setorId: colheita.setorId,
      corFitaId: colheita.corFitaId || undefined,
      dataColheita: colheita.dataColheita,
      quantidadeCachos: colheita.quantidadeCachos,
      observacoes: colheita.observacoes || "",
      usuarioId: colheita.usuarioId,
      semFita: (colheita as any).semFita || false,
    });
    setEditDialogOpen(true);
  };

  const handleDelete = (colheita: ColheitaWithRelations) => {
    setSelectedColheita(colheita);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedColheita) return;
    deleteMutation.mutate(selectedColheita.id);
  };

  // Filter and paginate colheitas
  const { filteredColheitas, paginatedColheitas, totalPages, totalFiltered, totalCachos } = useMemo(() => {
    if (!colheitas) return { filteredColheitas: [], paginatedColheitas: [], totalPages: 0, totalFiltered: 0, totalCachos: 0 };
    
    // Apply filters
    let filtered = [...colheitas];
    if (filterSetor !== "all") {
      filtered = filtered.filter(c => c.setorId === filterSetor);
    }
    if (filterCor !== "all") {
      filtered = filtered.filter(c => c.corFitaId === filterCor);
    }
    // Busca NO CLIENTE, dentro do mesmo memo dos outros filtros — a paginação e os
    // totais do rodapé seguem o que está na tela. Termo vazio passa tudo.
    if (busca.trim()) {
      filtered = filtered.filter(c => combina(busca, c.setor.nome, c.corFita?.nome));
    }
    if (filterStatus !== "all") {
      if (filterStatus === "CICLOS_ATIVOS") {
        // Filter by active cycles
        const ciclosAtivos = ciclos?.filter(ciclo => ciclo.status === "ATIVO").map(c => c.id) || [];
        filtered = filtered.filter(c => c.cicloId && ciclosAtivos.includes(c.cicloId));
      } else {
        filtered = filtered.filter(c => c.status === filterStatus);
      }
    }
    
    // Filter by "Sem Fita"
    if (filterSemFita !== "all") {
      const semFitaValue = filterSemFita === "true";
      filtered = filtered.filter(c => (c as any).semFita === semFitaValue);
    }
    
    // Filter by date range (data colheita)
    if (filterDataInicio) {
      const dataInicio = new Date(filterDataInicio + 'T00:00:00Z');
      filtered = filtered.filter(c => {
        const dataColheita = new Date(c.dataColheita + 'T00:00:00Z');
        return dataColheita >= dataInicio;
      });
    }
    if (filterDataFim) {
      const dataFim = new Date(filterDataFim + 'T00:00:00Z');
      filtered = filtered.filter(c => {
        const dataColheita = new Date(c.dataColheita + 'T00:00:00Z');
        return dataColheita <= dataFim;
      });
    }
    
    // Sort by date desc
    filtered.sort((a, b) => new Date(b.dataColheita).getTime() - new Date(a.dataColheita).getTime());
    
    const totalFiltered = filtered.length;
    const totalCachos = filtered.reduce((sum, c) => sum + c.quantidadeCachos, 0);
    const totalPages = Math.ceil(totalFiltered / itemsPerPage);
    
    // Paginate
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);
    
    return { filteredColheitas: filtered, paginatedColheitas: paginated, totalPages, totalFiltered, totalCachos };
  }, [colheitas, filterSetor, filterCor, filterStatus, filterSemFita, filterDataInicio, filterDataFim, busca, currentPage, itemsPerPage, ciclos]);

  const diasColheita = useMemo(() => {
    if (!filteredColheitas || !cores) return [];
    return agruparColheitasPorDia(
      filteredColheitas.filter(c => c.corFita) as any[],
      cores as any[]
    );
  }, [filteredColheitas, cores]);

  // Período exato dos dias exibidos em "Por dia" — usado só pra buscar o chip informativo
  // "vendido em #023" (venda_colheita_associacoes), sem precisar de um endpoint novo.
  const periodoDiasColheita = useMemo(() => {
    if (diasColheita.length === 0) return undefined;
    const datas = diasColheita.map(d => d.data).sort();
    return { inicio: datas[0], fim: datas[datas.length - 1] };
  }, [diasColheita]);

  const { data: colheitasPorDiaSetor } = useColheitasPorDiaSetor(periodoDiasColheita?.inicio, periodoDiasColheita?.fim);

  const associadoEmPorDiaSetor = useMemo(() => {
    const map = new Map<string, Array<{ vendaId: string; numero: string }>>();
    for (const g of colheitasPorDiaSetor ?? []) {
      if (g.associadoEm.length > 0) map.set(`${g.data}|${g.setorId}`, g.associadoEm);
    }
    return map;
  }, [colheitasPorDiaSetor]);

  // Safeguard: página maior que o total (o filtro encolheu a lista, ou veio de um
  // link com `?pagina=9` que não existe mais) volta para a última que existe.
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      definir({ pagina: totalPages });
    }
  }, [currentPage, totalPages, definir]);

  /** Mudar filtro sempre volta para a página 1 — o hook não adivinha isso. */
  const handleFilterChange = (type: "setor" | "cor" | "status" | "semFita" | "dataInicio" | "dataFim", value: string) => {
    const chave = type === "dataInicio" ? "de" : type === "dataFim" ? "ate" : type;
    definir({ [chave]: value, pagina: 1 });
  };

  const handleClearFilters = () => {
    definir({ setor: "all", cor: "all", status: "all", semFita: "all", de: "", ate: "", pagina: 1 });
  };

  const hasActiveFilters = filterSetor !== "all" || filterCor !== "all" || filterStatus !== "all" || filterSemFita !== "all" || filterDataInicio !== "" || filterDataFim !== "";

  // Export to Excel
  const handleExportToExcel = () => {
    if (!filteredColheitas || filteredColheitas.length === 0) {
      toast({
        title: "Nenhum dado para exportar",
        description: "Não há colheitas para exportar.",
        variant: "destructive",
      });
      return;
    }

    // Prepare data for export
    const exportData = filteredColheitas.map((colheita) => ({
      "Data Colheita": formatColheitaDate(colheita.dataColheita),
      "Setor": colheita.setor.nome,
      "Variedade": colheita.setor.variedade?.nome ?? "",
      "Cor da Fita": colheita.corFita?.nome || "Sem fita",
      "Quantidade": colheita.quantidadeCachos,
      "Status": colheita.status === "NO_PRAZO" ? "No Prazo" : 
                colheita.status === "ATRASADA" ? "Atrasada" : 
                colheita.status === "ANTECIPADA" ? "Antecipada" : "Sem Plano",
      "Fora Tolerância": colheita.foraTolerancia ? "Sim" : "Não",
      "Observações": colheita.observacoes || "",
    }));

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Colheitas");

    // Generate filename with current date
    const today = new Date().toISOString().split('T')[0];
    const filename = `colheitas_${today}.xlsx`;

    // Download file
    XLSX.writeFile(wb, filename);

    toast({
      title: "Exportação concluída!",
      description: `${filteredColheitas.length} registros exportados.`,
    });
  };

  // Um tom por estado, do sistema — antes eram cinco pares de classes fixas
  // (um par de classes fixas por status, com escala numérica) escritos aqui.
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "NO_PRAZO":
        return <StatusBadge tom="sucesso"><CheckCircle2 className="h-3 w-3 mr-1" />No prazo</StatusBadge>;
      case "ATRASADA":
        return <StatusBadge tom="erro"><AlertCircle className="h-3 w-3 mr-1" />Atrasada</StatusBadge>;
      case "ANTECIPADA":
        return <StatusBadge tom="info"><CheckCircle2 className="h-3 w-3 mr-1" />Antecipada</StatusBadge>;
      case "SEM_PLANO":
        return <StatusBadge tom="neutro"><XCircle className="h-3 w-3 mr-1" />Sem plano</StatusBadge>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4 md:space-y-8">
      <PageHeader
        titulo="Colheitas"
        descricao="Registro de cachos colhidos"
        acoesSecundarias={
          <Button
            variant="outline"
            onClick={handleExportToExcel}
            disabled={!filteredColheitas || filteredColheitas.length === 0}
            className={ALVO_TOQUE_BOTAO}
            data-testid="button-export-excel"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
        }
        acao={
          <Button className={ALVO_TOQUE_BOTAO} onClick={() => setCreateDialogOpen(true)} data-testid="button-new-colheita">
            <Plus className="h-4 w-4 mr-2" />
            Nova colheita
          </Button>
        }
      />

      <div className="space-y-3">
        <PageToolbar
          fim={
            <>
            <PageSearch
              valor={busca}
              onChange={v => definir({ q: v, pagina: 1 })}
              placeholder="Setor ou fita"
              testId="input-busca-colheitas"
            />
            <Segmented
              testId="seg-visualizacao"
              valor={visualizacao}
              onChange={v => definir({ visao: v })}
              opcoes={[
                { id: "lista", label: "Lista", icone: List },
                { id: "por-dia", label: "Por dia", icone: CalendarDays },
              ]}
            />
            </>
          }
        >
          <Select value={filterSetor} onValueChange={(v) => handleFilterChange("setor", v)}>
            <SelectTrigger className="w-[160px] shrink-0" data-testid="filter-setor">
              <SelectValue placeholder="Setor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Setores</SelectItem>
              {setoresProdutivos?.sort((a, b) => a.nome.localeCompare(b.nome)).map(setor => (
                <SelectItem key={setor.id} value={setor.id}>{setor.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterCor} onValueChange={(v) => handleFilterChange("cor", v)}>
            <SelectTrigger className="w-[150px] shrink-0" data-testid="filter-cor">
              <SelectValue placeholder="Cor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as Cores</SelectItem>
              {cores?.map(cor => (
                <SelectItem key={cor.id} value={cor.id}>{cor.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={(v) => handleFilterChange("status", v)}>
            <SelectTrigger className="w-[160px] shrink-0" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="CICLOS_ATIVOS">Ciclos Ativos</SelectItem>
              <SelectItem value="NO_PRAZO">No Prazo</SelectItem>
              <SelectItem value="ATRASADA">Atrasada</SelectItem>
              <SelectItem value="ANTECIPADA">Antecipada</SelectItem>
              <SelectItem value="SEM_PLANO">Sem Plano</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterSemFita} onValueChange={(v) => handleFilterChange("semFita", v)}>
            <SelectTrigger className="w-[140px] shrink-0" data-testid="filter-sem-fita">
              <SelectValue placeholder="Sem Fita" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="true">Sem Fita</SelectItem>
              <SelectItem value="false">Com Fita</SelectItem>
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              onClick={handleClearFilters}
              className="shrink-0"
              data-testid="button-clear-filters"
            >
              <X className="h-4 w-4 mr-1" />
              Limpar
            </Button>
          )}
        </PageToolbar>

        <PageToolbar
          fim={
            <div className="flex gap-3 text-xs text-muted-foreground whitespace-nowrap">
              <span data-testid="text-total-registros">
                {totalFiltered} {totalFiltered === 1 ? "registro" : "registros"}
              </span>
              <span className="text-muted-foreground">•</span>
              <span data-testid="text-total-cachos" className="font-medium">
                {totalCachos} {totalCachos === 1 ? "cacho" : "cachos"}
              </span>
            </div>
          }
        >
          <Label className="text-xs text-muted-foreground whitespace-nowrap">Período (Data Colheita):</Label>
          <Input
            type="date"
            value={filterDataInicio}
            onChange={(e) => handleFilterChange("dataInicio", e.target.value)}
            className="w-[150px] shrink-0 text-xs"
            data-testid="filter-data-inicio"
          />
          <span className="text-xs text-muted-foreground">até</span>
          <Input
            type="date"
            value={filterDataFim}
            onChange={(e) => handleFilterChange("dataFim", e.target.value)}
            className="w-[150px] shrink-0 text-xs"
            data-testid="filter-data-fim"
          />
        </PageToolbar>
      </div>

      {/* Por dia view */}
      {visualizacao === 'por-dia' && !colheitasLoading && (
        <div className="space-y-3" data-testid="view-por-dia">
          {diasColheita.length === 0 ? (
            busca.trim()
              ? <NadaEncontrado termo={busca} />
              : <EmptyState icone={CalendarDays} titulo="Nenhum registro encontrado" descricao="Nenhuma colheita no recorte escolhido." />
          ) : (
            diasColheita.map(dia => {
              const dataBR = dia.data.split('-').reverse().join('/');
              return (
                <DataCard
                  key={dia.data}
                  titulo={dataBR}
                  meta={
                    <>
                      <span className="font-mono">S{String(dia.semana).padStart(2, '0')}</span>
                      {' · '}
                      <span className="font-mono font-semibold text-foreground">{dia.totalCachos}</span> cachos
                      {' · '}
                      {dia.setores.length} setor{dia.setores.length !== 1 ? 'es' : ''}
                    </>
                  }
                  acao={
                    <>
                      <button
                        className="flex items-center gap-1.5 text-xs font-semibold border border-sucesso bg-sucesso-suave text-sucesso rounded-lg px-3 py-1.5 hover:bg-sucesso-suave transition-colors"
                        onClick={() => setDiaCompartilhar(dia)}
                        data-testid={`btn-compartilhar-${dia.data}`}
                      >
                        <Share2 className="h-3.5 w-3.5" />Compartilhar
                      </button>
                      <button
                        className="flex items-center gap-1.5 text-xs font-semibold border border-primary bg-primary text-primary-foreground rounded-lg px-3 py-1.5 hover:opacity-90 transition-opacity"
                        onClick={() => abrir(`/cacho/vendas/novo?data=${dia.data}`)}
                        data-testid={`btn-registrar-venda-${dia.data}`}
                      >
                        💰 Registrar venda
                      </button>
                    </>
                  }
                  testId={`dia-card-${dia.data}`}
                >
                  <div className="p-4 space-y-4">
                    {dia.setores.map(setor => {
                      const associadoEm = associadoEmPorDiaSetor.get(`${dia.data}|${setor.setorId}`) ?? [];
                      return (
                      <div key={setor.setorId}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-bold text-sm">{setor.nome}</span>
                          {setor.isMinoria && (
                            <span className="text-xs border rounded-full px-2 py-0.5 text-muted-foreground">
                              {setor.variedadeNome}
                            </span>
                          )}
                          {associadoEm.length > 0 && (
                            <span
                              className="text-[10px] font-semibold bg-atencao-suave text-atencao rounded-full px-2 py-0.5"
                              data-testid={`chip-vendido-${dia.data}-${setor.setorId}`}
                            >
                              {associadoEm.length === 1 ? `vendido em #${associadoEm[0].numero}` : `+${associadoEm.length} vendas`}
                            </span>
                          )}
                          <span className="ml-auto font-mono text-xs text-muted-foreground">{setor.totalCachos} cachos</span>
                        </div>
                        <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
                          {setor.fitas.map(fita => (
                            <div key={fita.corId} className="flex items-center gap-2 text-sm py-0.5">
                              <span className="w-2.5 h-2.5 rounded-full border border-black/20 flex-shrink-0" style={{ backgroundColor: fita.hexColor }} />
                              <span className="font-mono font-semibold">{fita.quantidade}</span>
                              <span className="text-muted-foreground">{fita.quantidade === 1 ? 'fita' : 'fitas'} {fita.corNome}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </DataCard>
              );
            })
          )}
        </div>
      )}

      {/* List view */}
      {visualizacao === 'lista' && (colheitasLoading ? (
        <PageSkeleton variante="tabela" linhas={6} />
      ) : !colheitas || colheitas.length === 0 ? (
        <EmptyState
          icone={ShoppingBag}
          titulo="Nenhuma colheita registrada ainda"
          descricao="Registre a primeira colheita para o ciclo começar a baixar e o rendimento por setor a existir."
          acao={
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />Nova colheita
            </Button>
          }
          testId="empty-colheitas"
        />
      ) : totalFiltered === 0 ? (
        busca.trim() ? (
          <NadaEncontrado termo={busca} />
        ) : (
          <EmptyState
            icone={Filter}
            titulo="Nenhuma colheita com os filtros aplicados"
            descricao="Nenhum registro no recorte de setor, cor, status e período escolhido."
            acao={<Button variant="ghost" onClick={handleClearFilters}>Limpar filtros</Button>}
            testId="empty-colheitas-filtro"
          />
        )
      ) : (
        <>
          <DataCard
            rodape={
              <>
                {totalFiltered} {totalFiltered === 1 ? "registro" : "registros"}
                {" · "}
                <b className="text-foreground">{totalCachos.toLocaleString("pt-BR")}</b> {totalCachos === 1 ? "cacho" : "cachos"}
              </>
            }
          >
            <div className="p-4 md:p-0">
              {/* Mobile: Cards View */}
              <div className="space-y-3 md:hidden">
                {paginatedColheitas?.map((colheita) => (
                  <div
                    key={colheita.id}
                    className="flex flex-col gap-3 p-3 border rounded-lg bg-card hover-elevate"
                    data-testid={`colheita-card-${colheita.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono flex-1">
                        <span>{new Date(colheita.dataColheita + 'T00:00:00Z').toLocaleDateString("pt-BR", { timeZone: 'UTC' })} / </span>
                        <span className="font-bold">S{getColheitaWeek(colheita.dataColheita)}</span>
                      </span>
                      {colheita.corFita ? (
                        <Badge
                          variant="secondary"
                          className="text-xs border"
                          style={{
                            backgroundColor: colheita.corFita.hexColor + "20",
                            borderColor: colheita.corFita.hexColor,
                          }}
                        >
                          {colheita.corFita.nome}
                        </Badge>
                      ) : (
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${(colheita as any).semFita ? "border-atencao/30 bg-atencao-suave text-atencao" : ""}`}
                        >
                          Sem fita
                        </Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">Setor</p>
                        <p className="font-medium">{colheita.setor.nome}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Variedade</p>
                        <p className="font-medium">{colheita.setor.variedade?.nome}</p>
                        {colheita.ciclo && (
                          <button
                            onClick={() => abrir(`/cacho/ciclos/${colheita.ciclo!.id}`)}
                            className="text-xs text-primary hover:underline cursor-pointer mt-1"
                            data-testid={`link-ciclo-${colheita.ciclo.id}`}
                          >
                            {colheita.ciclo.codigo}
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="text-xs">
                          <span className="text-muted-foreground">Qtd:</span>
                          <span className="font-mono font-medium ml-1">{colheita.quantidadeCachos}</span>
                        </div>
                        {getStatusBadge(colheita.status)}
                        {colheita.foraTolerancia && (
                          <Badge variant="secondary" className="bg-atencao-suave text-atencao border-atencao/20 text-xs">
                            Fora Tolerância
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-end gap-1 pt-2 border-t">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => handleEdit(colheita)}
                        data-testid={`button-edit-${colheita.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Pode cap="cacho.excluir">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => handleDelete(colheita)}
                          data-testid={`button-delete-${colheita.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </Pode>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: Table View */}
              <div className="hidden md:block">
                <DataTable colunas={["10rem", "8rem", null, "9rem", "7rem", "9rem", "8rem", "6rem"]}>
                  <DataTableHeader>
                    <DataTableRow>
                      <DataTableHead>Data colheita</DataTableHead>
                      <DataTableHead>Setor</DataTableHead>
                      <DataTableHead>Variedade</DataTableHead>
                      <DataTableHead>Cor da fita</DataTableHead>
                      <DataTableHead num>Quantidade</DataTableHead>
                      <DataTableHead>Status</DataTableHead>
                      <DataTableHead>Fora tolerância</DataTableHead>
                      <DataTableHead num>Ações</DataTableHead>
                    </DataTableRow>
                  </DataTableHeader>
                  <DataTableBody>
                    {paginatedColheitas?.map((colheita) => (
                      <DataTableRow key={colheita.id} data-testid={`colheita-row-${colheita.id}`}>
                        <DataTableNum className="text-left">
                          {new Date(colheita.dataColheita + 'T00:00:00Z').toLocaleDateString("pt-BR", { timeZone: 'UTC' })} / <span className="font-bold">S{getColheitaWeek(colheita.dataColheita)}</span>
                        </DataTableNum>
                        {/* O setor é a coluna que responde à pergunta da tela. */}
                        <DataTableCell destaque>{colheita.setor.nome}</DataTableCell>
                        <DataTableCell>
                          <p className="truncate">{colheita.setor.variedade?.nome}</p>
                          {colheita.ciclo && (
                            <button
                              onClick={() => abrir(`/cacho/ciclos/${colheita.ciclo!.id}`)}
                              className="text-xs text-primary hover:underline cursor-pointer"
                              data-testid={`link-ciclo-${colheita.ciclo.id}`}
                            >
                              {colheita.ciclo.codigo}
                            </button>
                          )}
                        </DataTableCell>
                        <DataTableCell>
                          {colheita.corFita ? (
                            /* Cor de fita é DADO: vem do banco por `style`, nunca por classe. */
                            <span className="inline-flex items-center gap-1.5">
                              <span
                                className="h-2.5 w-2.5 rounded-full border border-black/20 shrink-0"
                                style={{ backgroundColor: colheita.corFita.hexColor }}
                              />
                              {colheita.corFita.nome}
                            </span>
                          ) : (
                            <StatusBadge tom={(colheita as any).semFita ? "atencao" : "neutro"}>Sem fita</StatusBadge>
                          )}
                        </DataTableCell>
                        <DataTableNum>{colheita.quantidadeCachos}</DataTableNum>
                        <DataTableCell>
                          {getStatusBadge(colheita.status)}
                        </DataTableCell>
                        <DataTableCell>
                          {colheita.foraTolerancia
                            ? <StatusBadge tom="atencao">Sim</StatusBadge>
                            : <span className="text-muted-foreground text-sm">Não</span>}
                        </DataTableCell>
                        <DataTableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleEdit(colheita)}
                              data-testid={`button-edit-${colheita.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Pode cap="cacho.excluir">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleDelete(colheita)}
                                data-testid={`button-delete-${colheita.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </Pode>
                          </div>
                        </DataTableCell>
                      </DataTableRow>
                    ))}
                  </DataTableBody>
                </DataTable>
              </div>
            </div>
          </DataCard>

          {/* Pagination */}
        {totalPages > 1 && (
          <Card>
            <CardContent className="p-3 md:p-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Itens por página:</span>
                  <Select 
                    value={itemsPerPage.toString()} 
                    onValueChange={(v) => definir({ porPagina: Number(v), pagina: 1 })}
                  >
                    <SelectTrigger className="w-[80px]" data-testid="select-items-per-page">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Página {currentPage} de {totalPages}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => definir({ pagina: Math.max(1, currentPage - 1) })}
                      disabled={currentPage === 1}
                      data-testid="button-prev-page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => definir({ pagina: Math.min(totalPages, currentPage + 1) })}
                      disabled={currentPage === totalPages}
                      data-testid="button-next-page"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        </>
      ))}

      {/* Share dialog */}
      {diaCompartilhar && (
        <Dialog open={!!diaCompartilhar} onOpenChange={(open) => { if (!open) { setDiaCompartilhar(null); setCopiado(false); } }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Compartilhar no WhatsApp</DialogTitle>
            </DialogHeader>
            <pre className="whitespace-pre-wrap text-sm bg-muted rounded-lg p-4 max-h-72 overflow-y-auto font-sans text-foreground">
              {textoWhatsAppColheita(diaCompartilhar)}
            </pre>
            <DialogFooter className="flex gap-2 sm:justify-start">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  navigator.clipboard.writeText(textoWhatsAppColheita(diaCompartilhar!));
                  setCopiado(true);
                  setTimeout(() => setCopiado(false), 1500);
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                {copiado ? 'Copiado!' : 'Copiar texto'}
              </Button>
              <Button
                className="flex-1"
                onClick={() => {
                  const texto = textoWhatsAppColheita(diaCompartilhar!);
                  if (typeof navigator.share === 'function') {
                    navigator.share({ text: texto }).catch(() => {
                      window.open('https://wa.me/?text=' + encodeURIComponent(texto), '_blank');
                    });
                  } else {
                    window.open('https://wa.me/?text=' + encodeURIComponent(texto), '_blank');
                  }
                }}
              >
                <Share2 className="h-4 w-4 mr-2" />
                Abrir WhatsApp
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Create Dialog - New Multi-line Form */}
      <RegistrarColheitaDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open);
        if (!open) setIsReconciling(false);
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">Editar Colheita</DialogTitle>
            <DialogDescription>
              Atualize as informações do registro.
            </DialogDescription>
          </DialogHeader>

          {/* Banner de "Sem Fita" com opção de reconciliação */}
          {(selectedColheita as any)?.semFita && !isReconciling && (
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-atencao/50 bg-atencao-suave/50">
              <div className="flex items-center gap-2">
                <CircleOff className="h-5 w-5 text-atencao" />
                <div>
                  <p className="text-sm font-medium text-atencao">
                    Colheita registrada sem fita
                  </p>
                  <p className="text-xs text-atencao/80">
                    A fita foi encontrada? Reconcilie esta colheita.
                  </p>
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-atencao/30 text-atencao hover:bg-atencao-suave"
                onClick={() => setIsReconciling(true)}
                data-testid="button-reconcile"
              >
                Reconciliar
              </Button>
            </div>
          )}

          {/* Banner de reconciliação em progresso */}
          {isReconciling && (
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-sucesso/50 bg-sucesso-suave/50">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-sucesso" />
                <div>
                  <p className="text-sm font-medium text-sucesso">
                    Modo de Reconciliação
                  </p>
                  <p className="text-xs text-sucesso/80">
                    Selecione a cor da fita para associar a um ciclo.
                  </p>
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-muted-foreground"
                onClick={() => {
                  setIsReconciling(false);
                  editForm.setValue("corFitaId", undefined);
                  editForm.setValue("semFita", true);
                }}
                data-testid="button-cancel-reconcile"
              >
                Cancelar
              </Button>
            </div>
          )}

          <form onSubmit={editForm.handleSubmit(onEditSubmit)} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="edit-setorId">Setor *</Label>
                <Select
                  value={editForm.watch("setorId")}
                  onValueChange={(value) => editForm.setValue("setorId", value)}
                >
                  <SelectTrigger data-testid="edit-select-setor">
                    <SelectValue placeholder="Selecione o setor" />
                  </SelectTrigger>
                  <SelectContent>
                    {setoresProdutivos?.filter(s => s.ativo).map((setor) => (
                      <SelectItem key={setor.id} value={setor.id}>
                        <div>
                          <div className="font-medium">{setor.nome}</div>
                          <div className="text-xs text-muted-foreground">{setor.variedade?.nome}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-corFitaId">
                  Cor da Fita {isReconciling ? "*" : "(opcional)"}
                </Label>
                <Select
                  value={editForm.watch("corFitaId") || "_NONE_"}
                  onValueChange={(value) => {
                    editForm.setValue("corFitaId", value === "_NONE_" ? null as any : value);
                    // Se está reconciliando e selecionou uma cor, desativa semFita
                    if (isReconciling && value !== "_NONE_") {
                      editForm.setValue("semFita", false);
                    }
                  }}
                >
                  <SelectTrigger data-testid="edit-select-cor">
                    <SelectValue placeholder="Sem fita" />
                  </SelectTrigger>
                  <SelectContent>
                    {!isReconciling && <SelectItem value="_NONE_">Sem fita</SelectItem>}
                    {cores?.filter(c => c.ativo).map((cor) => (
                      <SelectItem key={cor.id} value={cor.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-4 h-4 rounded border"
                            style={{ backgroundColor: cor.hexColor }}
                          />
                          <span>{cor.nome}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-dataColheita">Data da Colheita *</Label>
                <Input
                  id="edit-dataColheita"
                  type="date"
                  className="font-mono"
                  data-testid="edit-input-data-colheita"
                  {...editForm.register("dataColheita")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-quantidadeCachos">Quantidade de Cachos *</Label>
                <Input
                  id="edit-quantidadeCachos"
                  type="number"
                  min="1"
                  data-testid="edit-input-quantidade"
                  {...editForm.register("quantidadeCachos", { valueAsNumber: true })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-observacoes">Observações</Label>
              <Textarea
                id="edit-observacoes"
                placeholder="Observações sobre a colheita..."
                data-testid="edit-input-observacoes"
                {...editForm.register("observacoes")}
              />
            </div>

            <DialogFooter className="flex-col-reverse md:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                data-testid="edit-button-cancel"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={updateMutation.isPending}
                data-testid="edit-button-submit"
              >
                {updateMutation.isPending ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta colheita? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-button-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-button-confirm"
            >
              {deleteMutation.isPending ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
